import React, { useEffect, useState } from "react";
import { GET_AGENCY_URL } from "../constant";
import axios from "axios";
import AgencyTable from "../components/tables/AgencyTable";
import { currentDateFormat } from "../utils";
import { useDispatch, useSelector } from "react-redux";
import { setLoading } from "../redux/loaderAction";
import Loader from "../components/loader/Loader";

const PAGE_LIMIT = 10;
const PRIMARY_COLOR = "#F4A442";
const SECONDARY_COLOR = "#2D5A50";

function AgencyList() {
  const [agencyDetails, setAgencyDetails] = useState([]);
  const [agencyCode, setAgencyCode] = useState("");
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    recordsPerPage: PAGE_LIMIT,
  });
  const dispatch = useDispatch();
  const loading = useSelector((state) => state.loader.loading);
  const [currentDate] = useState(new Date());

  const fetchAgencies = async (page = 1, agencyId = "") => {
    dispatch(setLoading(true));
    try {
      const response = await axios.get(GET_AGENCY_URL, {
        params: {
          page,
          limit: PAGE_LIMIT,
          agencyId,
        },
      });
      const ok = response?.data?.status === 200 || response?.data?.status === "200";
      if (ok) {
        const rows = Array.isArray(response?.data?.data) ? response.data.data : [];
        const p = response?.data?.pagination || {};
        setAgencyDetails(rows);
        setPagination({
          currentPage: p.currentPage ?? page,
          totalPages: Math.max(1, p.totalPages ?? 1),
          totalRecords: p.totalRecords ?? rows.length,
          recordsPerPage: p.recordsPerPage ?? PAGE_LIMIT,
        });
      } else {
        setAgencyDetails([]);
        setPagination((prev) => ({ ...prev, currentPage: page, totalPages: 1, totalRecords: 0 }));
      }
    } catch (error) {
      console.error(error);
      setAgencyDetails([]);
    } finally {
      dispatch(setLoading(false));
    }
  };

  useEffect(() => {
    fetchAgencies(currentPage, agencyCode.trim());
  }, [currentPage]);

  const handleAgencyCodeChange = (e) => {
    setAgencyCode(e.target.value.toUpperCase());
  };

  const handleSearch = async () => {
    setIsSearchLoading(true);
    setCurrentPage(1);
    try {
      await fetchAgencies(1, agencyCode.trim());
    } finally {
      setIsSearchLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage !== currentPage) {
      setCurrentPage(newPage);
    }
  };

  const { currentPage: pg, totalPages, totalRecords, recordsPerPage } = pagination;
  const startIndex = (pg - 1) * recordsPerPage;
  const endIndex = Math.min(startIndex + (agencyDetails?.length || 0), totalRecords);

  const getPageNumbers = () => {
    if (totalPages <= 4) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    return [1, 2, 3, "ellipsis", totalPages];
  };

  const pageNumbers = getPageNumbers();

  return (
    <>
      <section>
        <div className="d-flex justify-content-between form-container">
          <div>
            <h2 className="fs-5 font-weight-600">Agency List </h2>
          </div>
          <div>
            <span>{currentDateFormat(currentDate)}</span>
          </div>
        </div>
      </section>
      <section className="filter-section">
        <div className="d-flex justify-content-between filter-container">
          <div className="schedule-filter">
            <div className="me-2">
              <label style={{ display: "block" }}>Agency Code</label>
              <input
                type="text"
                name="pass_name"
                className="form-control"
                value={agencyCode}
                onChange={handleAgencyCodeChange}
              />
            </div>
            <div className="me-2">
              <label style={{ display: "block" }}>&nbsp;</label>
              <button
                className="btn btn-primary rounded-1"
                type="button"
                style={{
                  background: "#31876f",
                  border: "unset",
                  height: "38px",
                  color: "#fff",
                  padding: "7px 16px",
                }}
                onClick={handleSearch}
                disabled={isSearchLoading}
              >
                {isSearchLoading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    />
                    Searching...
                  </>
                ) : (
                  "Search"
                )}
              </button>
            </div>
          </div>
        </div>
      </section>
      <section>
        <div>
          <AgencyTable tableData={agencyDetails} />
        </div>
        {totalPages > 1 && (
          <div
            className="d-flex flex-column flex-sm-row align-items-center justify-content-between gap-3 px-4 py-3 mt-0"
            style={{
              backgroundColor: "rgba(248, 249, 250, 0.8)",
              border: "1px solid #e9ecef",
              borderTop: "none",
              borderRadius: "0 0 0.25rem 0.25rem",
            }}
          >
            <p className="text-sm text-muted mb-0">
              Showing{" "}
              <span style={{ color: SECONDARY_COLOR, fontWeight: 700 }}>
                {agencyDetails?.length ? startIndex + 1 : 0}-{endIndex}
              </span>{" "}
              of{" "}
              <span style={{ color: SECONDARY_COLOR, fontWeight: 700 }}>
                {totalRecords}
              </span>{" "}
              results
            </p>
            <div className="d-flex align-items-center gap-1">
              <button
                type="button"
                className="btn btn-link p-2 text-secondary border-0 shadow-none"
                style={{ opacity: pg === 1 ? 0.35 : 1 }}
                disabled={pg === 1}
                onClick={() => handlePageChange(1)}
                title="First page"
                aria-label="First page"
              >
                <i className="pi pi-angle-double-left" />
              </button>
              <button
                type="button"
                className="btn btn-link p-2 text-secondary border-0 shadow-none me-1"
                style={{ opacity: pg === 1 ? 0.35 : 1 }}
                disabled={pg === 1}
                onClick={() => handlePageChange(pg - 1)}
                title="Previous page"
                aria-label="Previous page"
              >
                <i className="pi pi-angle-left" />
              </button>
              <div className="d-flex align-items-center gap-1">
                {pageNumbers.map((p, i) =>
                  p === "ellipsis" ? (
                    <span key={`ellipsis-${i}`} className="px-2 text-muted">
                      ...
                    </span>
                  ) : (
                    <button
                      key={p}
                      type="button"
                      className="btn btn-sm fw-semibold rounded"
                      style={{
                        minWidth: 32,
                        height: 32,
                        backgroundColor: p === pg ? PRIMARY_COLOR : "transparent",
                        color: p === pg ? "#fff" : "#495057",
                        border: "none",
                        boxShadow: p === pg ? "0 1px 2px rgba(0,0,0,0.05)" : "none",
                      }}
                      onClick={() => handlePageChange(p)}
                      aria-label={`Page ${p}`}
                      aria-current={p === pg ? "page" : undefined}
                    >
                      {p}
                    </button>
                  )
                )}
              </div>
              <button
                type="button"
                className="btn btn-link p-2 text-secondary border-0 shadow-none ms-1"
                style={{ opacity: pg === totalPages ? 0.35 : 1 }}
                disabled={pg === totalPages}
                onClick={() => handlePageChange(pg + 1)}
                title="Next page"
                aria-label="Next page"
              >
                <i className="pi pi-angle-right" />
              </button>
              <button
                type="button"
                className="btn btn-link p-2 text-secondary border-0 shadow-none"
                style={{ opacity: pg === totalPages ? 0.35 : 1 }}
                disabled={pg === totalPages}
                onClick={() => handlePageChange(totalPages)}
                title="Last page"
                aria-label="Last page"
              >
                <i className="pi pi-angle-double-right" />
              </button>
            </div>
          </div>
        )}
      </section>
      {loading && <Loader />}
    </>
  );
}

export default AgencyList;
